"use client"

import clsx from "clsx"
import React, { JSX } from "react"
import { TText } from "./TText"

/**
 * @description 텍스트 목록. TText의 variant/color 체계를 목록에 적용
 * @example
 * <TTextList
 *   items={["항목 1", "항목 2", "항목 3"]}
 *   variant="content"
 *   color="secondary"
 *   bullet
 * />
 */
export interface TTextListProps {
	/** 목록 항목 배열 */
	items: Array<React.ReactNode | string>
	/**
	 * 텍스트 타이포그래피 variant
	 * @default "content"
	 */
	variant?: "page" | "display" | "headline" | "title" | "subtitle" | "content" | "caption" | "help" | "tiny"
	/**
	 * 텍스트 색상
	 * @default "secondary"
	 */
	color?: "text" | "primary" | "warning" | "error" | "secondary" | "tertiary" | "placeholder" | "disabled" | "black"
	/** 굵은 텍스트 */
	bold?: boolean
	/** 각 항목 앞 커스텀 아이콘 (bullet 미사용 시 적용) */
	icon?: React.ReactNode
	/** 불릿(disc) 표시 */
	bullet?: boolean
	/** 항목 간격 축소 (space-y-1) */
	dense?: boolean
	/** 간격 패딩 비활성화 */
	disablePadding?: boolean
	/** 추가 CSS 클래스 */
	className?: string
	/** 자동 번역 방지 (noTranslate 클래스 적용) */
	noTranslate?: boolean
}

export const TTextList = React.memo((props: TTextListProps): JSX.Element => {
	const { items, variant = "content", color = "secondary", bold, icon, bullet, dense, disablePadding, className, noTranslate } = props

	const ulClassName = React.useMemo(
		() => clsx(bullet ? "list-disc pl-4" : "list-none", !disablePadding && (dense ? "space-y-1" : "space-y-2"), "m-0", className),
		[bullet, disablePadding, dense, className]
	)

	const liClassName = React.useMemo(() => clsx("break-keep", { noTranslate }), [noTranslate])

	return (
		<ul className={ulClassName}>
			{items.map((item, i) => (
				<TText key={i} component="li" variant={variant} color={color} bold={bold} noTranslate={noTranslate} className={liClassName}>
					<div className="flex items-center space-x-2">
						{!bullet && icon ? icon : null}
						<span>{item}</span>
					</div>
				</TText>
			))}
		</ul>
	)
})

TTextList.displayName = "TTextList"
